import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import { homepageContentService } from '../../services/api/homepageContentService';
import type { HomepageContentFormData } from '../../types/homepage-content.types';

const HomepageContentForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<HomepageContentFormData>({
    content_type: 'carousel_banner',
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    sub_title: '',
    bullet_points: [],
    is_active: true,
    display_order: 0,
  });

  const [bulletPointInput, setBulletPointInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const contentTypes = [
    { value: 'carousel_banner', label: 'Carousel Banner' },
    { value: 'sidebar_image', label: 'Sidebar Image' },
    { value: 'welcome_section', label: 'Welcome Section' },
    { value: 'vision_section', label: 'Vision Section' },
    { value: 'objective_section', label: 'Objective Section' },
    { value: 'portal_logo', label: 'Portal Logo' },
    { value: 'header_logo', label: 'Header Logo' },
    { value: 'header_leader', label: 'Header Leader' },
  ];

  useEffect(() => {
    if (isEditMode && id) {
      fetchContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      console.log('Fetching content for ID:', id);
      const response = await homepageContentService.getById(Number(id));
      console.log('Response received:', response);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      const content = response.data;
      console.log('Setting form data:', content);
      
      setFormData({
        content_type: content.content_type,
        title: content.title,
        description: content.description || '',
        image_url: content.image_url || '',
        link_url: content.link_url || '',
        sub_title: content.sub_title || '',
        bullet_points: content.bullet_points || [],
        is_active: Boolean(content.is_active),
        display_order: content.display_order,
      });
      
      toast.success('Content loaded successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch content';
      toast.error(errorMessage);
      console.error('Error fetching content:', error);
      console.error('Error details:', error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddBulletPoint = () => {
    if (!bulletPointInput.trim()) {
      toast.error('Please enter a bullet point');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      bullet_points: [...(prev.bullet_points || []), bulletPointInput.trim()],
    }));
    setBulletPointInput('');
  };

  const handleRemoveBulletPoint = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      bullet_points: prev.bullet_points?.filter((_, i) => i !== index) || [],
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.content_type) {
      newErrors.content_type = 'Content type is required';
    }

    // Validate based on content type
    if (
      ['carousel_banner', 'sidebar_image', 'vision_section', 'objective_section', 'portal_logo', 'header_logo', 'header_leader'].includes(
        formData.content_type
      )
    ) {
      if (!formData.image_url?.trim()) {
        newErrors.image_url = 'Image URL is required for this content type';
      }
    }

    if (formData.content_type === 'portal_logo' && !formData.link_url?.trim()) {
      newErrors.link_url = 'Link URL is required for portal logos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        is_active: Boolean(formData.is_active),
        image_url: formData.image_url?.trim() || undefined,
        link_url: formData.link_url?.trim() || undefined,
      };
      if (isEditMode && id) {
        await homepageContentService.update(Number(id), submitData);
        toast.success('Content updated successfully');
      } else {
        await homepageContentService.create(submitData);
        toast.success('Content created successfully');
      }
      navigate('/homepage-content');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update content' : 'Failed to create content');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {loading && isEditMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-700">Loading content...</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/homepage-content')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Content' : 'Add New Content'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {isEditMode ? 'Update homepage content' : 'Create new homepage content'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Content Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Type <span className="text-red-500">*</span>
            </label>
            <select
              name="content_type"
              value={formData.content_type}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.content_type ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isEditMode}
            >
              {contentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.content_type && <p className="mt-1 text-sm text-red-500">{errors.content_type}</p>}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter title"
            />
            {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
          </div>

          {/* Sub Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sub Title</label>
            <input
              type="text"
              name="sub_title"
              value={formData.sub_title}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter sub title (optional)"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter description"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image
              {['carousel_banner', 'sidebar_image', 'vision_section', 'objective_section', 'portal_logo', 'header_logo', 'header_leader'].includes(
                formData.content_type
              ) && <span className="text-red-500"> *</span>}
            </label>
            <div className="flex items-center gap-4">
              <label
                className={`inline-flex items-center px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                  uploading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Upload size={18} className="mr-2" />
                {uploading ? 'Uploading...' : 'Choose Image'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      setUploading(true);
                      const response = await homepageContentService.uploadImage(file);
                      if (response.success && response.data) {
                        setFormData((prev) => ({ ...prev, image_url: response.data.url }));
                        if (errors.image_url) {
                          setErrors((prev) => ({ ...prev, image_url: '' }));
                        }
                        toast.success('Image uploaded successfully');
                      }
                    } catch (error: any) {
                      const msg = error.response?.data?.message || 'Failed to upload image';
                      toast.error(msg);
                    } finally {
                      setUploading(false);
                      e.target.value = '';
                    }
                  }}
                />
              </label>
              {uploading && (
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
            {errors.image_url && <p className="mt-1 text-sm text-red-500">{errors.image_url}</p>}
            {formData.image_url && (
              <div className="mt-3 relative inline-block">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="max-w-xs max-h-40 object-cover rounded border border-gray-200"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <p className="mt-1 text-xs text-gray-500 break-all">{formData.image_url}</p>
              </div>
            )}
          </div>

          {/* Link URL (for portal logos) */}
          {formData.content_type === 'portal_logo' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link URL <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="link_url"
                value={formData.link_url}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.link_url ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="https://example.com"
              />
              {errors.link_url && <p className="mt-1 text-sm text-red-500">{errors.link_url}</p>}
            </div>
          )}

          {/* Bullet Points */}
          {['welcome_section', 'vision_section', 'objective_section'].includes(formData.content_type) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bullet Points</label>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={bulletPointInput}
                  onChange={(e) => setBulletPointInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddBulletPoint();
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter bullet point and press Enter or click Add"
                />
                <button
                  type="button"
                  onClick={handleAddBulletPoint}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center"
                >
                  <Plus size={18} className="mr-1" />
                  Add
                </button>
              </div>

              {formData.bullet_points && formData.bullet_points.length > 0 && (
                <ul className="space-y-2">
                  {formData.bullet_points.map((point, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                      <div className="flex-1 text-sm text-gray-900">{point}</div>
                      <button
                        type="button"
                        onClick={() => handleRemoveBulletPoint(index)}
                        className="flex-shrink-0 text-red-600 hover:text-red-800 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
            <input
              type="number"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              min="0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0"
            />
            <p className="mt-1 text-sm text-gray-500">Lower numbers appear first</p>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
              Active (visible on homepage)
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/homepage-content')}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Content' : 'Create Content'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HomepageContentForm;